import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { toast } from "sonner";
import { UserPlus, Shield, Folder as FolderIcon, History, Users, Activity, Settings, Play, Target } from "lucide-react";

const ALL_KPIS = [
  { key: "cycle_time", label: "Cycle Time" },
  { key: "sla_compliance", label: "SLA Compliance" },
  { key: "throughput", label: "Throughput" },
  { key: "open_red_flags", label: "Open Red Flags" },
  { key: "avg_first_response_hours", label: "Avg First Response" },
  { key: "workload", label: "Workload" },
];

function KPIConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [cycle, setCycle] = useState("");
  const [sla, setSla] = useState("");
  const [firstR, setFirstR] = useState("");

  useEffect(() => {
    api.get("/settings/kpi-config").then(r => {
      setCfg(r.data);
      setCycle(String(r.data?.targets?.cycle_time_days ?? 21));
      setSla(String(r.data?.targets?.sla_compliance_pct ?? 90));
      setFirstR(String(r.data?.targets?.avg_first_response_hours ?? 24));
    }).catch(() => {});
  }, []);

  const toggle = (k) => {
    const enabled = cfg.enabled.includes(k)
      ? cfg.enabled.filter(x => x !== k)
      : [...cfg.enabled, k];
    setCfg({ ...cfg, enabled });
  };

  const save = async () => {
    try {
      const { data } = await api.put("/settings/kpi-config", {
        enabled: cfg.enabled,
        targets: {
          cycle_time_days: parseFloat(cycle) || 21,
          sla_compliance_pct: parseFloat(sla) || 90,
          avg_first_response_hours: parseFloat(firstR) || 24,
        },
      });
      setCfg(data);
      toast.success("KPI configuration saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!cfg) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="kpi-config-card">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-[#005289]" />
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">General Counsel KPI Dashboard</div>
      </div>
      <p className="text-sm text-slate-600 mb-3">
        Choose which KPIs display on the General Counsel's dashboard and set performance targets for the analyst / contract analyst / attorney team.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
        {ALL_KPIS.map(k => (
          <label key={k.key} className="flex items-center gap-2 text-xs border border-slate-200 rounded px-3 py-2 hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={cfg.enabled.includes(k.key)} onChange={() => toggle(k.key)}
                   data-testid={`kpi-toggle-${k.key}`} />
            <span className="font-semibold">{k.label}</span>
          </label>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <label className="text-xs font-semibold text-slate-700">
          <div className="mb-1">Cycle time target (days)</div>
          <input type="number" value={cycle} onChange={e => setCycle(e.target.value)}
                 className="w-full h-9 px-3 text-sm border border-slate-200 rounded" data-testid="kpi-target-cycle" />
        </label>
        <label className="text-xs font-semibold text-slate-700">
          <div className="mb-1">SLA compliance target (%)</div>
          <input type="number" value={sla} onChange={e => setSla(e.target.value)}
                 className="w-full h-9 px-3 text-sm border border-slate-200 rounded" data-testid="kpi-target-sla" />
        </label>
        <label className="text-xs font-semibold text-slate-700">
          <div className="mb-1">Avg first response target (hours)</div>
          <input type="number" value={firstR} onChange={e => setFirstR(e.target.value)}
                 className="w-full h-9 px-3 text-sm border border-slate-200 rounded" data-testid="kpi-target-first" />
        </label>
      </div>
      <button onClick={save} className="h-9 px-4 text-white rounded text-xs font-semibold"
              style={{ background: "#005289" }} data-testid="kpi-save">Save KPI configuration</button>
    </div>
  );
}

const ROLES = ["it_admin","general_counsel","legal_manager","attorney","contract_analyst","compliance","records_management","department_director","business_unit_rep"];

export default function AdminPanel() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [folders, setFolders] = useState([]);
  const [audit, setAudit] = useState([]);
  const [cronHealth, setCronHealth] = useState([]);
  const [cronHistory, setCronHistory] = useState([]);
  const [reminderDays, setReminderDays] = useState([]);
  const [reminderDraft, setReminderDraft] = useState("");
  const [runningCron, setRunningCron] = useState("");
  const [auditFilter, setAuditFilter] = useState("all");
  const [auditSearch, setAuditSearch] = useState("");

  const reloadCron = () => {
    api.get("/cron/health").then(r => setCronHealth(r.data)).catch(()=>{});
    api.get("/cron/history").then(r => setCronHistory(r.data)).catch(()=>{});
  };

  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data)).catch(()=>{});
    api.get("/folders").then(r => setFolders(r.data)).catch(()=>{});
    api.get("/audit").then(r => setAudit(r.data)).catch(()=>{});
    api.get("/settings/reminder-schedule").then(r => {
      setReminderDays(r.data.days_before_end || []);
      setReminderDraft((r.data.days_before_end || []).join(", "));
    }).catch(()=>{});
    reloadCron();
  }, []);

  const [newUser, setNewUser] = useState({ email: "", name: "", role: "contract_analyst", title: "", department: "" });

  const createUser = async () => {
    if (!newUser.email || !newUser.name) return toast.error("Email and name required");
    try {
      await api.post("/users", newUser);
      toast.success("User created");
      setNewUser({ email: "", name: "", role: "contract_analyst", title: "", department: "" });
      const r = await api.get("/users"); setUsers(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); const r = await api.get("/users"); setUsers(r.data); }
    catch { toast.error("Failed"); }
  };

  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); const r = await api.get("/users"); setUsers(r.data); toast.success("Role updated"); }
    catch { toast.error("Failed"); }
  };

  const runCronNow = async (name) => {
    setRunningCron(name);
    try {
      await api.post(`/cron/${name}/run-now`);
      toast.success(`Triggered ${name} — refreshing status`);
      setTimeout(reloadCron, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setTimeout(() => setRunningCron(""), 3000); }
  };

  const saveReminderSchedule = async () => {
    const days = reminderDraft.split(",").map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n > 0 && n <= 730);
    if (!days.length) return toast.error("Enter one or more positive numbers (comma-separated)");
    try {
      const { data } = await api.put("/settings/reminder-schedule", { days_before_end: days });
      setReminderDays(data.days_before_end);
      setReminderDraft(data.days_before_end.join(", "));
      toast.success(`Renewal reminders will fire at ${data.days_before_end.join(", ")} days before end.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-page">
      <div>
        <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#005289]" /><h1 className="text-2xl font-bold text-slate-900">IT Administration</h1></div>
        <p className="text-sm text-slate-500 mt-1">Manage users, roles, folder ACLs, system settings, cron health & the audit log.</p>
      </div>

      <div className="border-b border-slate-200 flex gap-4">
        {[
          { k: "users", label: "Users", icon: Users },
          { k: "folders", label: "Folder ACLs", icon: FolderIcon },
          { k: "cron", label: "Cron Health", icon: Activity },
          { k: "settings", label: "System Settings", icon: Settings },
          { k: "audit", label: "Audit Log", icon: History },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  className={`px-1 py-3 text-xs font-semibold border-b-2 flex items-center gap-1.5 ${tab === t.k ? "border-[#005289] text-[#005289]" : "border-transparent text-slate-500"}`}
                  data-testid={`admin-tab-${t.k}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Add User</div>
            <div className="grid grid-cols-5 gap-2">
              <input placeholder="Email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="h-9 px-3 text-sm border border-slate-200 rounded" data-testid="new-user-email" />
              <input placeholder="Full name" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="h-9 px-3 text-sm border border-slate-200 rounded" data-testid="new-user-name" />
              <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})} className="h-9 px-2 text-sm border border-slate-200 rounded" data-testid="new-user-role">
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
              <input placeholder="Title" value={newUser.title} onChange={e => setNewUser({...newUser, title: e.target.value})} className="h-9 px-3 text-sm border border-slate-200 rounded" />
              <button onClick={createUser} className="h-9 px-3 text-white rounded text-xs font-semibold" style={{ background: "#005289" }} data-testid="create-user-btn">
                <UserPlus className="w-3.5 h-3.5 inline mr-1" /> Add
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table className="hcd-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Title</th><th>Department</th><th>Status</th><th className="num">Actions</th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td className="font-semibold">{u.name}</td>
                    <td className="text-slate-600">{u.email}</td>
                    <td>
                      <select value={u.role} onChange={e => changeRole(u, e.target.value)} className="h-7 text-xs border border-slate-200 rounded px-1" data-testid={`role-${u.email}`}>
                        {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                    </td>
                    <td className="text-slate-500 text-xs">{u.title || "—"}</td>
                    <td className="text-slate-500 text-xs">{u.department || "—"}</td>
                    <td>{u.active ? <span className="chip chip-ontrack"><span className="dot" />Active</span> : <span className="chip chip-draft"><span className="dot" />Deactivated</span>}</td>
                    <td className="num">
                      <button onClick={() => toggleActive(u)} className="text-xs px-2 py-1 hover:bg-slate-50 rounded">
                        {u.active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "folders" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="hcd-table">
            <thead><tr><th>Folder</th><th>Parent</th><th>Department</th><th className="num">ACLs</th></tr></thead>
            <tbody>
              {folders.map(f => (
                <tr key={f.id}>
                  <td className="font-semibold">{f.name}</td>
                  <td className="text-slate-500 text-xs">{f.parent_id || "root"}</td>
                  <td className="text-slate-600">{f.department || "—"}</td>
                  <td className="num text-xs">{f.acls?.length || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "cron" && (
        <div className="space-y-4" data-testid="admin-cron-tab">
          <div className="grid grid-cols-3 gap-4">
            {cronHealth.map(c => (
              <div key={c.name} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`cron-${c.name}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700">{c.name}</div>
                  <span className={`chip ${c.status === "healthy" ? "chip-ontrack" : c.status === "error" ? "chip-overdue" : c.status === "stale" ? "chip-duesoon" : "chip-draft"}`}>
                    <span className="dot" />{c.status.replace(/_/g, " ")}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500">Last ran</div>
                <div className="text-sm font-semibold text-slate-900">{c.last_ran_at ? new Date(c.last_ran_at).toLocaleString() : "Never"}</div>
                {c.last_result && (
                  <div className="mt-3 text-[11px] text-slate-600 space-y-0.5">
                    {c.last_result.emails_sent != null && <div>Emails sent: <b>{c.last_result.emails_sent}</b></div>}
                    {c.last_result.scanned_flags != null && <div>Flags scanned: {c.last_result.scanned_flags}</div>}
                    {c.last_result.scanned_timers != null && <div>Timers scanned: {c.last_result.scanned_timers}</div>}
                    {c.last_result.overdue != null && <div>Overdue: {c.last_result.overdue}</div>}
                    {c.last_result.avg_cycle_days != null && <div>Avg cycle: {c.last_result.avg_cycle_days}d</div>}
                    {c.last_result.error && <div className="text-red-600 font-bold">Error: {c.last_result.error}</div>}
                  </div>
                )}
                <button onClick={() => runCronNow(c.name)} disabled={runningCron === c.name}
                        className="mt-3 w-full h-8 px-3 text-white rounded text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
                        style={{ background: "#00AE42" }} data-testid={`run-cron-${c.name}`}>
                  <Play className="w-3 h-3" />{runningCron === c.name ? "Triggered..." : "Run Now"}
                </button>
              </div>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">Run history (last 200)</div>
            <table className="hcd-table">
              <thead><tr><th>Cron</th><th>Ran At</th><th className="num">Emails</th><th className="num">Scanned</th><th>Notes</th></tr></thead>
              <tbody>
                {cronHistory.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-sm">No cron runs yet.</td></tr>}
                {cronHistory.map((r, i) => (
                  <tr key={i}>
                    <td className="font-semibold">{r.name}</td>
                    <td className="text-xs">{new Date(r.ran_at).toLocaleString()}</td>
                    <td className="num">{r.emails_sent ?? "—"}</td>
                    <td className="num">{r.scanned_flags ?? r.scanned_timers ?? "—"}</td>
                    <td className="text-xs text-slate-600">
                      {r.total != null && <span>total={r.total} overdue={r.overdue} avg_cycle={r.avg_cycle_days ?? "—"}d</span>}
                      {r.error && <span className="text-red-600 font-bold ml-2">ERR: {r.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="space-y-4" data-testid="admin-settings-tab">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Renewal Reminder Schedule</div>
            <p className="text-sm text-slate-600 mb-3">
              Days before contract renewal_date when the daily cron will email attorneys, the Director of Legal Team, and department directors.
              Enter one or more positive integers (1-730), comma-separated. Current: <b>{reminderDays.length ? reminderDays.join(", ") : "—"}</b>
            </p>
            <div className="flex items-center gap-2">
              <input value={reminderDraft} onChange={e => setReminderDraft(e.target.value)}
                     placeholder="e.g. 120, 90, 60, 30"
                     className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded" data-testid="reminder-days-input" />
              <button onClick={saveReminderSchedule} className="h-9 px-4 text-white rounded text-xs font-semibold"
                      style={{ background: "#005289" }} data-testid="reminder-days-save">Save schedule</button>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              These milestones are used by the renewal-check cron (runs daily at 8am UTC) and can also be triggered manually from the Cron Health tab.
            </div>
          </div>

          <KPIConfigCard />
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-3" data-testid="admin-audit-tab">
          <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-2 flex-wrap">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mr-2">Filter</div>
            {["all","contract","user","folder","cron","settings","reports"].map(rt => (
              <button key={rt} onClick={() => setAuditFilter(rt)}
                      className={`h-7 px-3 text-xs font-semibold rounded ${auditFilter === rt ? "bg-[#005289] text-white" : "border border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`audit-filter-${rt}`}>
                {rt.toUpperCase()}
              </button>
            ))}
            <input value={auditSearch} onChange={e => setAuditSearch(e.target.value)}
                   placeholder="Search actor or action..."
                   className="ml-auto h-8 px-3 text-xs border border-slate-200 rounded w-64" data-testid="audit-search" />
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table className="hcd-table">
              <thead><tr><th>Time</th><th>Actor</th><th>Role</th><th>Resource</th><th>Contract</th><th>Action</th><th>Meta</th></tr></thead>
              <tbody>
                {audit
                  .filter(a => auditFilter === "all" || a.resource_type === auditFilter)
                  .filter(a => !auditSearch || (
                    (a.actor || "").toLowerCase().includes(auditSearch.toLowerCase()) ||
                    (a.action || "").toLowerCase().includes(auditSearch.toLowerCase())
                  ))
                  .slice(0, 500).map((a, i) => (
                  <tr key={i}>
                    <td className="text-xs text-slate-500">{a.at ? new Date(a.at).toLocaleString() : "—"}</td>
                    <td className="font-semibold text-xs">{a.actor}</td>
                    <td className="text-[10px] text-slate-500">{a.actor_role ? a.actor_role.replace(/_/g, " ") : "—"}</td>
                    <td className="text-[10px]"><span className="chip chip-draft"><span className="dot" />{a.resource_type || "contract"}</span></td>
                    <td className="font-mono text-xs">{a.contract_number || "—"}</td>
                    <td className="text-xs text-slate-700">{a.action}</td>
                    <td className="text-[10px] text-slate-500 max-w-[300px] truncate" title={a.meta ? JSON.stringify(a.meta) : ""}>
                      {a.meta && Object.keys(a.meta).length ? JSON.stringify(a.meta) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
